import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import {
  MoneyCell,
  SkuCell,
  TransactionLineTable,
  type LineColumn,
} from '../components/TransactionLineTable.tsx';
import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, num, statusClass, statusLabel } from '../lib/format.ts';

interface Line {
  lineNo: number;
  productCode: string | null;
  description: string | null;
  qtyOrdered: number;
  qtyDelivered: number;
  unitPrice: number;
  subTotal: number;
  location: string | null;
}

interface Detail {
  id: number;
  soNo: string;
  soDate: string;
  customerNo: string | null;
  customerName: string | null;
  salesman: string | null;
  currency: string;
  value: number;
  status: string;
  ref: string | null;
  lines: Line[];
}

export function SalesOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['sales-order', id],
    queryFn: () =>
      apiFetch<Detail>(`/api/dashboard/sales-orders/${encodeURIComponent(id ?? '')}`),
    enabled: !!id,
  });

  const productLines = (data?.lines ?? []).filter((l) => l.productCode);
  const columns: LineColumn<Line>[] = [
    { label: '#', className: 'mono', render: (l) => l.lineNo },
    { label: 'SKU', className: 'mono', render: (l) => <SkuCell code={l.productCode} /> },
    { label: 'Description', render: (l) => l.description || '—' },
    { label: 'Loc', className: 'mono', render: (l) => l.location || '—' },
    { label: 'Qty ordered', className: 'num', render: (l) => num(l.qtyOrdered) },
    { label: 'Delivered', className: 'num', render: (l) => num(l.qtyDelivered) },
    { label: 'Unit price', className: 'num', render: (l) => <MoneyCell amount={l.unitPrice} currency={data?.currency ?? 'MYR'} /> },
    { label: 'Subtotal', className: 'num', render: (l) => <MoneyCell amount={l.subTotal} currency={data?.currency ?? 'MYR'} /> },
  ];

  return (
    <>
      <Link to="/sales-orders" className="back-link">← All sales orders</Link>
      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}
      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">SO {data.soNo}</h1>
              <span className="main-subtitle mono">{dateOnly(data.soDate)}</span>
            </div>
            <span className={`badge ${statusClass(data.status)}`}>{statusLabel(data.status)}</span>
          </header>
          <section className="panel">
            <header className="panel-header"><h2 className="panel-title">Header</h2></header>
            <dl className="detail-grid">
              <dt>SO no</dt><dd className="mono">{data.soNo}</dd>
              <dt>Date</dt><dd>{dateOnly(data.soDate)}</dd>
              <dt>Customer</dt>
              <dd>
                {data.customerNo ? (
                  <Link to={`/customers/${encodeURIComponent(data.customerNo)}`}>
                    {data.customerName ?? data.customerNo} <span className="mono" style={{ color: 'var(--text-dim)' }}>({data.customerNo})</span>
                  </Link>
                ) : '—'}
              </dd>
              <dt>Salesman</dt><dd>{data.salesman || '—'}</dd>
              <dt>Reference</dt><dd className="mono">{data.ref || '—'}</dd>
              <dt>Value</dt><dd>{money(data.value, data.currency)}</dd>
            </dl>
          </section>
          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Line items</h2>
              <span className="panel-subtitle">{productLines.length} item{productLines.length === 1 ? '' : 's'}</span>
            </header>
            <TransactionLineTable lines={productLines} columns={columns} />
          </section>
        </>
      )}
    </>
  );
}
